







































import { computed, defineComponent, PropType } from "@vue/composition-api";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { ContentFeed, ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import { getFilesIcon, isMediaFile, isVideoFile } from "@/lib/fileHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { format } from "@/lib/time";
import BaseChip from "@/components/BaseChip";
import BaseIcon from "@/components/BaseIcon";
import BaseImage from "@/components/BaseImage";
import ContentCover from "@/components/ContentCover";

function isContentFeed(f: ContentFeed | ContentSet): f is ContentFeed {
  return "files" in f;
}

export default defineComponent({
  name: "ContentFeedCover",
  components: { BaseImage, ContentCover, BaseIcon, BaseChip },
  props: {
    postData: { type: Object as PropType<ContentFeed | ContentSet>, required: true },
    small: { type: Boolean, default: false },
    hideIcon: { type: Boolean, default: false },
    authUserId: { type: String, default: "" },
    currentAuthorities: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props) {
    const coverFile = computed(() =>
      isContentFeed(props.postData) ? props.postData.files.find(f => isMediaFile(f)) : props.postData.cover
    );

    const coverSrc = computed(() => {
      if (!coverFile.value) return "";
      const method = isVideoFile(coverFile.value) ? "getVideoThumbnail" : "getImageThumbnail";
      return MediaFilesController[method](coverFile.value.urlPart, "480p");
    });

    const isAuthor = computed(() => props.authUserId === props.postData.userId);

    const showErrorIcon = computed(() => isAuthor.value && props.postData.processingStatus === "ERROR");
    const showProcessingChip = computed(
      () => isAuthor.value && ["NEW", "CONVERTING"].indexOf(props.postData.processingStatus) > -1
    );
    const showScheduledChip = computed(
      () =>
        isAuthor.value && !!props.postData.scheduleTime && parseISO(props.postData.scheduleTime).getTime() >= Date.now()
    );
    const isNotPublished = computed(() => isAuthor.value && !props.postData.publishTime);

    const showGradient = computed(
      () => showErrorIcon.value || showProcessingChip.value || showScheduledChip.value || isNotPublished.value
    );

    const scheduledText = computed(() =>
      props.postData.scheduleTime ? format(parseISO(props.postData.scheduleTime), "MM.dd.yy") : ""
    );

    const mainIcon = computed(() => {
      if (showErrorIcon.value) return "sci-eye-off";
      if (showProcessingChip.value) return "sci-processing";
      if (showScheduledChip.value) return "sci-watch";
      return "sci-eye-off";
    });

    const icon = computed(() => {
      if (props.postData.type === ContentFeedTypeEnum.EVENT) return "sci-calendar";
      if (props.postData.type === ContentFeedTypeEnum.STORY) return "sci-story";
      if (props.postData.type === ContentFeedTypeEnum.MEDIA && isContentFeed(props.postData)) {
        return getFilesIcon(props.postData.files);
      }
      return "sci-file-multiple";
    });

    const showLocked = computed(() =>
      isContentFeed(props.postData) &&
      checkAuthorities(props.currentAuthorities, isAuthor.value ? ["updateMyContentSet"] : ["updateAnyContentSet"])
        ? props.postData.subscriptionLevelId !== "0" || props.postData.pricePackageId !== "0"
        : false
    );

    return {
      coverSrc,
      icon,
      showGradient,
      showErrorIcon,
      showProcessingChip,
      showScheduledChip,
      scheduledText,
      mainIcon,
      showLocked,
    };
  },
});
